import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UploadStatus, ValidatorService } from '@shared/models/fileupload';
import * as JSZip from 'jszip';
import { map, Observable, Subject, throwError } from 'rxjs';
import { DocsService, IAsset, IPdfOutput, IStaticOutput, ITemplateCreateData, ITemplateUploadData, IXmlOutput } from '../docs.service';

@Injectable({
	providedIn: 'root',
})
export class TemplateValidatorService implements ValidatorService {
	private statusSubject: Subject<UploadStatus> = new Subject<UploadStatus>();
	public statusSubject$ = this.statusSubject.asObservable();

	constructor(
		private translate: TranslateService,
		private docsService: DocsService,
		private router: Router,
		private http: HttpClient,
	) {}

	validate(files: File[], fields: object[]) {
		const templateName = fields['template_name'].getRawValue().trim();
		let error = templateName.length > 100; // Validate form fields here
		if (error) {
			const errorMessage = this.translate.instant('docs.templates.validate.template_name_too_long');
			this.statusSubject.next({ status: 'client_error', data: errorMessage });
			return;
		}
		const version = Number(fields['template_version'].getRawValue());
		const defaultVersion = Number(fields['template_defaultversion'].getRawValue());
		error = defaultVersion > version;
		if (error) {
			const errorMessage = this.translate.instant('docs.templates.validate.template_defaultversion_greater');
			this.statusSubject.next({ status: 'client_error', data: errorMessage });
			return;
		}
		error = validateTemplateTags(fields['template_tags'].getRawValue().trim());
		if (error) {
			const errorMessage = this.translate.instant('tags.validate.invalid');
			this.statusSubject.next({ status: 'client_error', data: errorMessage });
			return;
		}
		if (files && files.length > 0) {
			console.log(`TemplateValidator: Processing file: ${files[0].name}`);
			const theFile = files[0];
			const reader = new FileReader();
			const fileList: string[] = [];
			const selectedOutputType = fields['template_outputtype'].getRawValue().trim();
			const normalizedOutputType = selectedOutputType.toLowerCase();
			reader.onload = () => {
				JSZip.loadAsync(reader.result)
					.then((zip) => {
						for (const [filename] of Object.entries(zip.files)) {
							if (filename.length > 0 && !filename.endsWith('/')) {
								fileList.push(filename);
								console.log(filename);
							}
						}
						const zipError = this.validateTemplateStructure(fileList, normalizedOutputType);
						if (zipError) {
							this.statusSubject.next({
								status: 'error',
								data: zipError,
							});
							return;
						}

						let output: IPdfOutput | IStaticOutput | IXmlOutput;
						switch (normalizedOutputType) {
							case 'pdf':
								output = this.createPdfOutput(fileList, fields);
								break;
							case 'static':
								output = this.createStaticOutput(fileList);
								break;
							case 'xml':
								output = this.createXmlOutput(fileList);
								break;
							default:
								this.statusSubject.next({
									status: 'client_error',
									data: this.translate.instant('docs.templates.validate.invalid_output_type'),
								});
								return;
						}

						let data: any;
						if (fields['template_version'].getRawValue() > 1) {
							data = this.createTemplateUpdateData(templateName, output, fields);
						} else {
							data = this.createTemplateCreateData(templateName, output, fields);
						}

						console.log(JSON.stringify(data));

						this.docsService
							.createTemplate(data)
							.pipe(map((res) => res.result))
							.subscribe({
								next: (saveRes) => {
									this.docsService
										.getUploadUrl(saveRes.version.self)
										.pipe(map((res) => res.result))
										.subscribe({
											next: (res) => {
												this.docsService.uploadAssetsFile(theFile, res.url).subscribe({
													next: (uploadStatus) => {
														this.statusSubject.next({
															status: 'success',
															data: uploadStatus,
														});
													},
													error: (error: Error) => {
														console.log('upload error', error);
														this.statusSubject.next({
															status: 'error',
															data: error,
														});
													},
												});
											},
											error: (error: Error) => {
												console.log('upload error', error);
												this.statusSubject.next({
													status: 'error',
													data: error,
												});
											},
										});
								},
								error: (error: Error) => {
									console.log('upload error', error);
									this.statusSubject.next({ status: 'error', data: error });
								},
							});
					})
					.catch((error) => {
						console.error('Failed to open zip file', error);
						this.statusSubject.next({ status: 'error', data: error });
					});
			};
			reader.onerror = function (err) {
				console.error('Failed to read file', err);
			};
			reader.readAsArrayBuffer(theFile);
		} else {
			const data = this.createTemplateUpdateData(templateName, null, fields);

			console.log(JSON.stringify(data));

			this.docsService
				.createTemplate(data)
				.pipe(map((res) => res.result))
				.subscribe({
					next: (saveRes) => {
						this.statusSubject.next({
							status: 'success',
							data: saveRes,
						});
					},
					error: (error: Error) => {
						console.log('upload error', error);
						this.statusSubject.next({
							status: 'error',
							data: error,
						});
					},
				});
		}
	}

	createTemplateCreateData(templateName: string, output: any, fields: object[]): ITemplateCreateData {
		return {
			name: templateName,
			description: fields['template_description'].getRawValue().trim(),
			outputs: [output],
			version: 1,
			defaultVersion: 1,
			tags: createTemplateTags(fields['template_tags'].getRawValue().trim()),
		};
	}

	createTemplateUpdateData(templateName: string, output: any, fields: object[]): ITemplateUploadData {
		return {
			name: templateName,
			description: fields['template_description'].getRawValue().trim(),
			input: { validation: '{}' },
			outputs: output ? [output] : [],
			defaultVersion: Number(fields['template_defaultversion'].getRawValue()),
			tags: createTemplateTags(fields['template_tags'].getRawValue().trim()),
		};
	}

	createStaticOutput(fileList: string[]): IStaticOutput {
		const name = `output${fileList[0].indexOf('.') > 0 ? `.${fileList[0].split('.').pop()}` : ''}`;

		return {
			name,
			asset: {
				path: fileList[0],
				sortIndex: 0,
				type: 'Placeholder',
			},
			type: 'Static',
			tags: {},
		};
	}

	createXmlOutput(fileList: string[]): IXmlOutput {
		const name = `output${fileList[0].indexOf('.') > 0 ? `.${fileList[0].split('.').pop()}` : ''}`;

		return {
			name,
			asset: {
				path: fileList[0],
				sortIndex: 0,
				type: 'Placeholder',
			},
			type: 'Xml',
			tags: {},
		};
	}

	createPdfOutput(fileList: string[], fields: object[]): IPdfOutput {
		return {
			name: 'output',
			layout: {
				assets: createAssetsList(fileList),
				body: {
					content: '',
					contentFileName: 'html/body.html',
					style: '',
					stylesheetFileName: this.getStylesheetReference(fileList, 'body'),
				},
				footer: {
					content: '',
					contentFileName: 'html/footer.html',
					style: '',
					stylesheetFileName: this.getStylesheetReference(fileList, 'footer'),
				},
				header: {
					content: '',
					contentFileName: 'html/header.html',
					style: '',
					stylesheetFileName: this.getStylesheetReference(fileList, 'header'),
				},
				margins: {
					left: 0,
					right: 0,
					top: Number(fields['template_outputmargin_top'].getRawValue()),
					bottom: Number(fields['template_outputmargin_bottom'].getRawValue()),
				},
				landscape: fields['template_outputlayout'].getRawValue() === this.translate.instant('docs.templates.dialog.orientation_landscape'),
				format: fields['template_outputformat'].getRawValue(),
			},
			type: 'Pdf',
			tags: {},
		};
	}

	getStylesheetReference(fileList: string[], section: string): string {
		const sectionPath = `html/${section}.css`;
		const defaultPath = 'html/stylesheet.css';

		if (fileList.includes(sectionPath)) {
			return sectionPath;
		}

		if (fileList.includes(defaultPath)) {
			return defaultPath;
		}

		return '';
	}

	validateTemplateStructure(fileList: string[], type: string): string {
		if (type === 'pdf') {
			if (!fileList.includes('html/body.html')) {
				return this.translate.instant('docs.templates.validate.pdf_missing_body_file');
			}
			if (!fileList.includes('html/footer.html')) {
				return this.translate.instant('docs.templates.validate.pdf_missing_footer_file');
			}
			if (!fileList.includes('html/header.html')) {
				return this.translate.instant('docs.templates.validate.pdf_missing_header_file');
			}
			if (fileList.findIndex((element) => element.includes('attachments/')) !== -1) {
				if (fileList.findIndex((element) => element.includes('attachments/prefix/')) === -1) {
					if (fileList.findIndex((element) => element.includes('attachments/postfix/')) === -1) {
						return this.translate.instant('docs.templates.validate.pdf_illegal_attachments_files');
					}
				}
				if (fileList.findIndex((element) => element.includes('attachments/postfix/')) === -1) {
					if (fileList.findIndex((element) => element.includes('attachments/prefix/')) === -1) {
						return this.translate.instant('docs.templates.validate.pdf_illegal_attachments_files');
					}
				}
			}
		} else if (type === 'static') {
			if (fileList.length > 1) {
				const errorMessage = this.translate.instant('docs.templates.validate.static_only_one_file');
				return errorMessage;
			}
		} else if (type === 'xml') {
			if (fileList.length !== 1) {
				return this.translate.instant('docs.templates.validate.xml_only_one_file');
			}

			if (!fileList[0].toLowerCase().endsWith('.xml')) {
				return this.translate.instant('docs.templates.validate.xml_only_one_file');
			}
		}
		return '';
	}

	handleError(error): Observable<never> {
		const msg = `${error?.status}: ${error?.message}`;
		return throwError(() => new Error(msg));
	}
}
function createAssetsList(fileList: string[]): IAsset[] {
	const assets: IAsset[] = [];
	fileList.sort();
	let sortIndexPostfix = 1;
	let sortIndexPrefix = 1;
	fileList.forEach((element) => {
		if (!element.startsWith('html/') && !element.startsWith('attachments/')) {
			const asset: IAsset = {
				path: element,
				sortIndex: 0,
				type: 'Placeholder',
			};
			assets.push(asset);
		}
		if (element.startsWith('attachments/prefix')) {
			const asset: IAsset = {
				path: element,
				sortIndex: sortIndexPrefix,
				type: 'Prefix',
			};
			assets.push(asset);
			sortIndexPrefix++;
		}
		if (element.startsWith('attachments/postfix')) {
			const asset: IAsset = {
				path: element,
				sortIndex: sortIndexPostfix,
				type: 'Postfix',
			};
			assets.push(asset);
			sortIndexPostfix++;
		}
	});
	return assets;
}

function createTemplateTags(tags: string): any {
	const tagList: string[] = tags.split(',');
	const tagObj: any = {};
	tagList.forEach((element) => {
		const keyValue: string[] = element.split('=');
		tagObj[keyValue[0].trim()] = keyValue[1].trim();
	});
	return tagObj;
}

function validateTemplateTags(tags: string): boolean {
	return !/^(?!.*(,|^)\s*(\s*\w+\s*)=[\w\s;.]*\1.*\b\2=[\w\s;.]+\b)(\s*\w[\w\s]*=[\w\s;.]+(?:,\s*\w[\w\s]*=[\w\s;.]+)*)(?<!\s)$/.test(tags);
}
